import React, { useState, useEffect, useRef } from "react";
import { MdEdit } from "react-icons/md";
import { useModal } from "../../Context/ModalContext";
import NoteQuickForm from "../QuickFormComponents/NoteQuickForm/NoteQuickForm";
import ReactDOM from "react-dom";
import styles from "./NoteCardItem.module.css";
import DateTimeService from "../../Services/datetimeService";
import { MdDelete } from "react-icons/md";
import ClientService from "../../Services/clientService";
import HorseService from "../../Services/horseService";
import ServiceOccurenceService from "../../Services/serviceOccurenceService";
import { addToast } from "../../Services/toastService";
import i18n from "../../Services/i18n";
import { useDeletionConfirmationModal } from "../../Hooks/DeletionConfirmationModal";
import noteIcon from "../../Assets/Icons/note.png";

const NoteCardItem = ({ note, type, refresh }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const editFormRef = useRef(null);
  const { openModal } = useModal();

  const confirmDelete = useDeletionConfirmationModal();
  const getDeletionFunction = () => {
    switch (type) {
      case "client":
        return (noteExternalId) => ClientService.DeleteNote(noteExternalId);
      case "horse":
        return (noteExternalId) => HorseService.DeleteNote(noteExternalId);
      case "serviceoccurence":
        return (noteExternalId) => ServiceOccurenceService.DeleteNote(noteExternalId);
      default:
        addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
        return () => {
          console.log("error getDeletionFunction");
        };
    }
  };

  const getUpdateFunction = () => {
    switch (type) {
      case "client":
        return (noteExternalId, data) => ClientService.UpdateNote(noteExternalId, data);
      case "horse":
        return (noteExternalId, data) => HorseService.UpdateNote(noteExternalId, data);
      case "serviceoccurence":
        return (noteExternalId, data) => ServiceOccurenceService.UpdateNote(noteExternalId, data);
      default:
        addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
        return async () => {};
    }
  };

  const ActionMenu = ({ isVisible }) => (
    <div className={`${styles.actionMenu} ${isVisible ? styles.visible : ""}`}>
      <MdEdit
        className={styles.actionMenuEdit}
        onClick={() => {
          openModal({
            title: i18n.t("NoteModal.EditTitle"),
            hideSaveButton: true,
            form: "formNoteEdit",
            canClose: async () => {
              const isDirty = editFormRef.current?.isDirty?.();
              if (!isDirty) return true;

              return window.confirm(i18n.t("Modal.UnsavedChangesConfirm"));
            },
            content: (
              <NoteQuickForm
                key={`edit-${note.externalId}`}
                ref={editFormRef}
                initialValue={{
                  note: note?.note ?? "",
                  noteDate: note?.noteDate,
                  color: note?.color ?? "#FFFFFF",
                  isCritical: !!note?.isCritical,
                  isPinned: !!note?.isPinned,
                }}
              />
            ),
            onSave: async () => {
              try {
                const isValid = editFormRef.current.validate();
                if (!isValid) return;

                const data = editFormRef.current.getFormData();
                const result = await getUpdateFunction()(note.externalId, data);

                if (result) {
                  if (typeof refresh === "function") await refresh();
                  return true;
                } else {
                  return false;
                }
              } catch (ex) {
                addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
                return false;
              }
            },
          });
        }}
      />
      <MdDelete
        className={styles.actionMenuDelete}
        onClick={() => {
          confirmDelete({
            entityId: note.externalId,
            icon: noteIcon,
            entityTitle: null,
            entityImage: null,
            deleteFunction: getDeletionFunction(),
            onClose: async (refreshNeeded) => {
              if (refreshNeeded && typeof refresh === "function") {
                await refresh();
              }
            },
          });
        }}
      />
    </div>
  );

  return (
    <>
      <div
        style={{ backgroundColor: note?.color }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onDoubleClick={() => setIsViewerOpen(true)}
        className={`${styles.item} ${isHovered ? styles.itemHovered : ""}`}
        title={i18n.t("NoteCardItem.DoubleClickToOpen")}>
        {note?.isCritical && (
          <div
            className={styles.criticalBadge}
            aria-label={i18n.t("NoteCardItem.CriticalLabel")}
            title={i18n.t("NoteCardItem.CriticalTooltip")}>
            <span className={styles.criticalEmoji} aria-hidden="true">
              🔥
            </span>
            <span className={styles.criticalText}>{i18n.t("NoteCardItem.Critical")}</span>
          </div>
        )}

        <ActionMenu isVisible={isHovered} />

        <div className={styles.contentContainer}>
          <div className={styles.textContainer}>
            <div className={styles.cardDate}>
              {DateTimeService.formatDateString(note.noteDate, true, true)}
            </div>
            <div
              className={styles.cardContent}
              dangerouslySetInnerHTML={{ __html: note?.note ?? "" }}
            />
          </div>
        </div>
      </div>

      {isViewerOpen && <NoteViewer note={note} onClose={() => setIsViewerOpen(false)} />}
    </>
  );
};

export default NoteCardItem;

/* ================= Viewer en portal ================ */

const NoteViewer = ({ note, onClose }) => {
  // Fermer avec Échap
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleOverlayClick = () => onClose();
  const handleContentClick = (e) => e.stopPropagation();

  return ReactDOM.createPortal(
    <div className={styles.noteModalOverlay} onClick={handleOverlayClick}>
      <button
        className={styles.noteCloseButton}
        onClick={onClose}
        aria-label={i18n.t("NoteViewer.Close")}
        title={i18n.t("NoteViewer.Close")}>
        &times;
      </button>

      <div className={styles.noteModalContent} onClick={handleContentClick}>
        <div className={styles.noteModalCard} style={{ backgroundColor: note?.color || "white" }}>
          <div className={styles.noteModalHeader}>
            <div className={styles.noteModalMeta}>
              <span className={styles.noteModalDate}>
                {DateTimeService.formatDateString(note.noteDate, true, true)}
              </span>
              {note?.isCritical && (
                <span
                  className={styles.noteModalBadge}
                  title={i18n.t("NoteCardItem.CriticalTooltip")}>
                  🔥 {i18n.t("NoteCardItem.Critical")}
                </span>
              )}
              {note?.isPinned && (
                <span className={styles.noteModalBadge} title={i18n.t("NoteViewer.PinnedTooltip")}>
                  📌 {i18n.t("NoteQuickForm.IsPinned")}
                </span>
              )}
            </div>
          </div>

          <div
            className={styles.noteModalBody}
            dangerouslySetInnerHTML={{ __html: note?.note ?? "" }}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};
